import { defineStore } from 'pinia'
import { useInstanceStore } from '@/store/backendInstances'
import backendHandler from '@/BackendAPI'
import IncidentsAssignedToMeSyncable from '@/syncing/syncables/IncidentsAssignedToMeSyncable.js'
import DeadAnimalCountsSyncable from '@/syncing/syncables/DeadAnimalCountsSyncable.js'
import { getDurationInDaysFromTimestamp } from '@/composables/datetime'
import PenStateDurationSyncable from '@/syncing/syncables/PenStateDurationSyncable.js'
import { DateTime, Duration } from 'luxon'
import { isClosedPenState } from '@/composables/incidents/incidentPenStateHelper'
import SyncableHandler from '@/syncing/SyncableHandler.js'
import { buildDNF } from '@/composables/helperFunctions.js'

export const useIncidentStore = defineStore('incidents', {
  state: () => ({
    incidentsAssignedToMe: {},
    deadAnimalCounts: {},
    penStateDuration: {},
  }),
  persist: false,
  share: {
    enable: true,
    initialize: true,
  },
  getters: {
    getIncidentsAssignedToMe(state) {
      return (globalUniqueIdentifier = useInstanceStore().selectedInstanceId) => state.incidentsAssignedToMe[globalUniqueIdentifier]
    },
    getDeadAnimalCounts(state) {
      return (globalUniqueIdentifier = useInstanceStore().selectedInstanceId) => state.deadAnimalCounts[globalUniqueIdentifier]
    },
    getPenStateDuration(state) {
      return (globalUniqueIdentifier = useInstanceStore().selectedInstanceId) => state.penStateDuration[globalUniqueIdentifier]
    },
  },
  actions: {
    /**
     * Loads all incidents of a specific type that are 'open' at a specific timestamp.
     * 'Open' means that the firstEventTimestamp of the incident is before the timestamp and the lastEventTimestamp is after the timestamp,
     * or that the firstEventTimestamp of the incident is before the timestamp and the incident is not closed yet.
     * @param {String} incidentType - The type of incident to load
     * @param {Number} timestamp - Unix Timestamp (ms) at which the incident should be open
     * @param globalUniqueIdentifier
     * @returns {Promise<Array>} - Array of Incidents
     */
    async loadIncidentsOpenAtSpecificTimestamp(
      incidentType,
      timestamp,
      additionalFilters = [],
      globalUniqueIdentifier = undefined,
      signal = undefined
    ) {
      const query = [
        [
          { key: 'type', operator: '==', value: incidentType },
          { key: 'firstEventTimestamp', operator: '<=', value: timestamp },
          { key: 'lastEventTimestamp', operator: '>=', value: timestamp },
        ],
        [
          { key: 'type', operator: '==', value: incidentType },
          { key: 'firstEventTimestamp', operator: '<=', value: timestamp },
          { key: 'state', operator: '!=', value: 'closed' },
        ],
      ]
      for (let i = 0; i < query.length; i++) {
        additionalFilters.forEach(filter => query[i].push(filter))
      }
      const incidentResponse = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/incidents/query',
        data: { query },
        signal,
      })
      if (!incidentResponse.ok) return null
      return incidentResponse.data
    },

    /**
     * Load All Incidents of a specific incident type with a firstEventTimestamp before a specific timestamp
     * @param {String} incidentType - The type of incident to load
     * @param {Number} timestamp - Unix Timestamp (ms) the incidents should be created before
     * @returns {Promise<Array>} - Array of Incidents
     */
    async loadIncidentsCreatedBeforeSpecificTimestamp(incidentType, timestamp, additionalQuery, projection) {
      const query = [
        { key: 'type', operator: '==', value: incidentType },
        { key: 'firstEventTimestamp', operator: '<=', value: timestamp },
      ]
      if (additionalQuery) {
        query.push(...additionalQuery)
      }
      const payload = { query }
      if (projection) payload.projection = projection
      const incidentResponse = await backendHandler.post(useInstanceStore().selectedInstanceId, {
        path: '/api/incidents/query',
        data: payload,
      })
      return incidentResponse.data
    },

    /**
     * Load Incidents of specified Incident Types that have their firstEventTimestamp in the specified timerange
     * @param {Array<String>} incidentTypes
     * @param {Number} startTimestamp
     * @param {Number} endTimestamp
     * @param {Object} projection
     * @param {Object} order
     * @param {String} globalUniqueIdentifier
     * @returns {Promise<Array>}
     */
    async loadIncidentsCreatedBetweenSpecificTimestamps(
      incidentTypes,
      startTimestamp,
      endTimestamp,
      projection,
      order,
      additionalFilters = [],
      globalUniqueIdentifier = undefined
    ) {
      const query = [
        [
          { key: 'firstEventTimestamp', operator: '>=', value: startTimestamp },
          { key: 'firstEventTimestamp', operator: '<=', value: endTimestamp },
        ],
      ]

      if (incidentTypes.length) {
        for (let i = 0; i < query.length; i++) {
          query[i].push({ key: 'type', operator: 'in', value: incidentTypes })
        }
      }
      for (let i = 0; i < query.length; i++) {
        additionalFilters.forEach(filter => query[i].push(filter))
      }
      return await this.queryIncidents({ query: query, projection, order }, globalUniqueIdentifier)
    },

    /**
     * Load Incidents of specified Incident Types that have their firstEventTimestamp in the specified timerange and are either not closed yet or have their lastEventTimestamp in the specified timerange
     * @param {Array<String>} incidentTypes
     * @param {Number} startTimestamp
     * @param {Number} endTimestamp
     * @param {Object|undefined} [projection]
     * @param {Object|undefined} [order]
     * @param additionalFilters
     * @param {Array | undefined} wrappedOrFilters
     * @param {String} globalUniqueIdentifier
     * @param signal AbortSignal to cancel the request if needed
     * @returns {Promise<Array>}
     */
    async loadIncidentsOpenBetweenSpecificTimestamps(
      incidentTypes,
      startTimestamp,
      endTimestamp,
      projection = undefined,
      order = undefined,
      additionalFilters = [], //Treated as simple AND filters
      wrappedOrFilters = undefined, //Treated as OR filter-groups
      globalUniqueIdentifier = undefined,
      signal
    ) {
      const dnfInput = [
        [
          [
            { key: 'firstEventTimestamp', operator: '<=', value: endTimestamp },
            { key: 'lastEventTimestamp', operator: '>=', value: startTimestamp },
          ],
          [
            { key: 'firstEventTimestamp', operator: '<=', value: endTimestamp },
            { key: 'state', operator: '!=', value: 'closed' },
          ],
        ],
      ]
      if (wrappedOrFilters && wrappedOrFilters.length) dnfInput.push(...wrappedOrFilters)
      if (additionalFilters && additionalFilters.length) dnfInput.push(...additionalFilters)
      if (incidentTypes && incidentTypes.length) dnfInput.push(incidentTypes.map(type => [{ key: 'type', operator: '==', value: type }]))

      const query = buildDNF(...dnfInput)

      return await this.queryIncidents({ query: query, projection, order }, globalUniqueIdentifier, signal)
    },

    /**
     * Generic Incident Query
     * @param payload
     * @param globalUniqueIdentifier
     * @param abortSignal
     * @returns {Promise<*|null>}
     */
    async queryIncidents(payload, globalUniqueIdentifier = undefined, abortSignal = undefined) {
      if (!globalUniqueIdentifier) globalUniqueIdentifier = useInstanceStore().selectedInstanceId
      const incidentResponse = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/incidents/query',
        data: payload,
        signal: abortSignal,
        priority: 2,
      })
      if (!incidentResponse.ok) return null
      return incidentResponse.data
    },

    /**
     * Generic Incident Query for one incident
     * @param payload
     * @param globalUniqueIdentifier
     * @returns {Promise<*|null>}
     */
    async queryOneIncident(payload, globalUniqueIdentifier = undefined) {
      if (!globalUniqueIdentifier) globalUniqueIdentifier = useInstanceStore().selectedInstanceId
      const incidentResponse = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/incidents/queryOne',
        data: payload,
      })
      if (!incidentResponse.ok) return null
      return incidentResponse.data
    },

    /**
     * Retrieve a single latest open pen state from the incident store based on a query.
     * It returns the latest open pen state based on the global unique identifier.
     *
     * @param {string} globalUniqueIdentifier - The global unique identifier of the instance.
     * @returns {object} - The latest open pen state from the incident store or null if there is no pen state.
     */
    async loadLatestOpenPenState(globalUniqueIdentifier = undefined) {
      const query = {
        query: [
          {
            key: 'type',
            operator: '==',
            value: 'penState',
          },
          {
            key: 'state',
            operator: '!=',
            value: 'closed',
          },
        ],
        limit: 1,
        order: { lastEventTimestamp: 'desc' },
      }

      const latestPenState = await this.queryOneIncident(query, globalUniqueIdentifier)

      return latestPenState
    },

    /**
     * Retrieve a single latest closed pen state from the incident store based on a query.
     * It returns the latest closed pen state based on the global unique identifier.
     *
     * @param {string} globalUniqueIdentifier - The global unique identifier of the instance.
     * @returns {object} - The latest closed pen state from the incident store or null if there is no pen state.
     */
    async loadLatestClosedPenState(excludedIncidentId = undefined, globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      const query = {
        query: [
          {
            key: 'type',
            operator: '==',
            value: 'penState',
          },
          {
            key: 'state',
            operator: '==',
            value: 'closed',
          },
        ],
        limit: 1,
        order: { closedAt: 'desc' },
      }

      if (excludedIncidentId) {
        query.query.push({
          key: 'incidentId',
          operator: '!=',
          value: excludedIncidentId,
        })
      }

      return this.queryOneIncident(query, globalUniqueIdentifier)
    },

    /**
     * Retrieve a single pen state from the incident store.
     * This function prioritizes the open pen state over the closed pen state and returns it.
     *
     * @param {string} globalUniqueIdentifier - The global unique identifier of the instance.
     * @returns {object} - The latest pen state from the incident store or null if there is no pen state. Priority is given to the open pen state.
     */
    async loadLatestPenState(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      const openPenState = await this.loadLatestOpenPenState(globalUniqueIdentifier)
      if (openPenState) return openPenState
      return this.loadLatestClosedPenState(false, globalUniqueIdentifier)
    },

    /**
     * Retrieve the duration between the latest pen state and now.
     *
     * @param {object} penState - The pen state incident.
     * @returns {object} - This object contains the open or closed state and the duration of the pen state in days.
     */
    loadPenStateDuration(penState) {
      if (!penState) return null

      const isClosed = isClosedPenState(penState)

      const duration = isClosed
        ? getDurationInDaysFromTimestamp(penState.firstEventTimestamp, penState.lastEventTimestamp)
        : getDurationInDaysFromTimestamp(penState.firstEventTimestamp)

      const daysGoneBySinceMovingOut = isClosed ? getDurationInDaysFromTimestamp(penState.lastEventTimestamp) : undefined

      return {
        state: penState.state,
        duration,
        daysGoneBySinceMovingOut,
        firstEventTimestamp: penState.firstEventTimestamp,
        lastEventTimestamp: penState.lastEventTimestamp,
        incidentId: penState.incidentId,
      }
    },

    /**
     * Load Specific Incident
     * @param incidentId
     * @returns {Promise<*|null>}
     */
    async getIncident(incidentId) {
      const incidentResponse = await backendHandler.get(useInstanceStore().selectedInstanceId, { path: '/api/incidents/' + incidentId })
      if (!incidentResponse.ok) return null
      return incidentResponse.data
    },

    /**
     * Load All Events for a specific Incident
     * @param incidentId
     * @param additionalFilters
     * @returns {Promise<*|null>}
     */
    async loadEventsForIncident(incidentId, additionalFilters = [], limit = undefined) {
      const query = [{ key: 'incidentId', operator: '==', value: incidentId }, ...additionalFilters]
      const incidentEventResponse = await backendHandler.post(useInstanceStore().selectedInstanceId, {
        path: '/api/incidentEvents/query',
        data: { query, order: { eventTimestamp: 'desc' }, limit },
      })
      if (!incidentEventResponse.ok) return null
      return incidentEventResponse.data
    },

    async loadEventsForIncidentWithpayload(payload) {
      const incidentEventResponse = await backendHandler.post(useInstanceStore().selectedInstanceId, {
        path: '/api/incidentEvents/query',
        data: payload,
      })
      if (!incidentEventResponse.ok) return null
      return incidentEventResponse.data
    },

    async loadFirstEventForIncident(incidentId) {
      const query = [{ key: 'incidentId', operator: '==', value: incidentId }]
      const incidentEventResponse = await backendHandler.post(useInstanceStore().selectedInstanceId, {
        path: '/api/incidentEvents/queryOne',
        data: { query, order: { eventTimestamp: 'asc' } },
      })
      if (!incidentEventResponse.ok) return null
      return incidentEventResponse.data
    },
    async loadCommentsForIncident(incidentId) {
      const query = [
        { key: 'incidentId', operator: '==', value: incidentId },
        { key: 'incidentEventId', operator: 'exists', value: false },
      ]
      const commentResponse = await backendHandler.post(useInstanceStore().selectedInstanceId, {
        path: '/api/incidentComments/query',
        data: { query, order: { creationTimestamp: 'asc' } },
      })
      if (!commentResponse.ok) return null
      return commentResponse.data
    },

    /**
     * Load Event Comments for a specific Incident Event
     * @param incidentId
     * @param incidentEventId
     * @returns {Promise<*|null>}
     */
    async loadEventCommentsForIncidentEvent(incidentId, incidentEventId) {
      const query = [
        { key: 'incidentId', operator: '==', value: incidentId },
        {
          key: 'incidentEventId',
          operator: '==',
          value: incidentEventId,
        },
      ]
      const commentResponse = await backendHandler.post(useInstanceStore().selectedInstanceId, {
        path: '/api/incidentComments/query',
        data: {
          query,
          order: [{ incidentEventId: 'asc' }, { creationTimestamp: 'asc' }],
        },
      })
      if (!commentResponse.ok) return null
      return commentResponse.data
    },

    /**
     * Create a new Comment for either an incident or an incident event
     * @param text
     * @param incidentId
     * @param incidentEventId
     * @param parentCommentId
     * @returns {Promise<void>}
     */
    async createNewIncidentComment(text, incidentId, incidentEventId = undefined, parentCommentId = undefined) {
      const payload = { incidentComment: { text } }
      if (incidentEventId) payload.incidentComment.incidentEventId = incidentEventId
      if (parentCommentId) payload.incidentComment.parentCommentId = parentCommentId
      await backendHandler.post(useInstanceStore().selectedInstanceId, {
        path: '/api/incidentComments/' + incidentId,
        data: payload,
      })
    },

    /**
     * Update the text of a comment
     * @param incidentCommentId
     * @param text
     * @returns {Promise<void>}
     */
    async updateCommentText(incidentCommentId, text) {
      const payload = { commentText: text }
      await backendHandler.patch(useInstanceStore().selectedInstanceId, {
        path: '/api/incidentComments/' + incidentCommentId,
        data: payload,
      })
    },

    /**
     * Delete a comment
     * @param incidentCommentId
     * @returns {Promise<void>}
     */
    async deleteComment(incidentCommentId) {
      await backendHandler.delete(useInstanceStore().selectedInstanceId, {
        path: '/api/incidentComments/' + incidentCommentId,
      })
    },

    /**
     * Restore a deleted comment
     * @param incidentCommentId
     * @returns {Promise<void>}
     */
    async restoreComment(incidentCommentId) {
      await backendHandler.put(useInstanceStore().selectedInstanceId, {
        path: '/api/incidentComments/' + incidentCommentId,
      })
    },

    /**
     * Add or remove a reaction for a comment
     * @param incidentCommentId
     * @param unicodeString
     * @returns {Promise<void>}
     */
    async addOrRemoveReactionForComment(incidentCommentId, unicodeString) {
      await backendHandler.put(useInstanceStore().selectedInstanceId, {
        path: '/api/incidentComments/' + incidentCommentId + '/' + unicodeString,
      })
    },

    /**
     * Get the schema for a specific incident type. This returns the NON - Simplified schema.
     * @param incidentType
     * @returns {Promise<*|null>}
     */
    async getIncidentSchema(incidentType) {
      const schemaResponse = await backendHandler.get(useInstanceStore().selectedInstanceId, {
        path: '/api/incidents/handlers/' + incidentType + '/false',
      })
      if (schemaResponse.ok) return schemaResponse.data
      return null
    },

    /**
     * Get the schema for a specific incident type. This returns the simplified schema.
     * @param incidentType
     * @returns {Promise<*|null>}
     */
    async getSimplifiedIncidentSchema(incidentType) {
      const schemaResponse = await backendHandler.get(useInstanceStore().selectedInstanceId, {
        path: '/api/incidents/handlers/' + incidentType + '/true',
      })
      if (schemaResponse.ok) return schemaResponse.data
      return null
    },

    async triggerSpecificIncident(incidentId, incidentData) {
      const response = await backendHandler.post(useInstanceStore().selectedInstanceId, {
        path: '/api/incidents/retroactive/' + incidentId,
        data: { incidentData },
      })
      if (!response.ok) return null
      return response.data
    },
    /**
     * Generic Trigger for an incident
     * @param incidentData
     * @returns {Promise<*|null>}
     */
    async triggerGenericIncident(incidentData) {
      const incidentResponse = await backendHandler.post(useInstanceStore().selectedInstanceId, {
        path: '/api/incidents',
        data: { incidentData: incidentData },
      })
      if (!incidentResponse.ok) return null
      return incidentResponse.data
    },

    async triggerDeadAnimal(payload) {
      const response = await backendHandler.post(useInstanceStore().selectedInstanceId, {
        path: '/api/deadAnimals/detection',
        data: payload,
      })
      if (!response.ok) return null
      return response.data
    },

    async markDeadAnimalAsRemoved(incidentId, removedTimestamp) {
      const response = backendHandler.post(useInstanceStore().selectedInstanceId, {
        path: '/api/deadAnimals/markRemoved/' + incidentId,
        data: { timestamp: removedTimestamp },
      })
      if (!response.ok) return null
      return response.data
    },

    async markDeadAnimalAsNotFalsePositive(incidentId) {
      const response = await backendHandler.post(useInstanceStore().selectedInstanceId, {
        path: '/api/deadAnimals/revertFalsePositive/' + incidentId,
      })
      if (!response.ok) return null
      return response.data
    },

    async markDeadAnimalAsFalsePositive(incidentId, falsePositiveTimestamp) {
      const response = await backendHandler.post(useInstanceStore().selectedInstanceId, {
        path: '/api/deadAnimals/falsePositive/' + incidentId,
        data: { timestamp: falsePositiveTimestamp },
      })
      if (!response.ok) return null
      return response.data
    },

    /**
     * Queries the incident-store to count the number of dead animals.
     *
     * @param {string} globalUniqueIdentifier - The global unique identifier of the instance.
     * @param {number} firstEventTimestamp - The first event timestamp to query.
     * @param {number} lastEventTimestamp - The last event timestamp to query.
     * @returns {Promise<number>} A promise that resolves to the count of dead animals.
     */
    async queryDeadAnimalCount(firstEventTimestamp, lastEventTimestamp, globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      return await this.loadIncidentsCreatedBetweenSpecificTimestamps(
        ['deadAnimal'],
        firstEventTimestamp,
        lastEventTimestamp,
        'count',
        undefined,
        [
          {
            key: 'messagePayload.falsePositive',
            operator: 'exists',
            value: false,
          },
        ],
        globalUniqueIdentifier
      )
    },

    /**
     * Calculates the deviation in dead animal count between the current and previous epoch.
     *
     * @param {Object} currentPenstate - The current pen state object.
     * @param {Object} previousPenState - The previous pen state object.
     * @param {number} currentDeadAnimalCount - The dead animal count of the current epoch.
     *
     * @returns {Promise<number|null>}
     * Amount of dead animals that have died in the last epoch, relative to the time past in the current epoch.
     */
    async getDeadAnimalDeviation(
      currentPenState,
      previousPenState,
      currentDeadAnimalCount,
      globalUniqueIdentifier = useInstanceStore().selectedInstanceId
    ) {
      if (!currentPenState || !previousPenState || currentDeadAnimalCount === undefined || currentDeadAnimalCount === null) return null

      /*
       * if elapsed time of current epoch is longer than previous epoch,
       * compare the entire current epoch to the entire previous epoch
       *
       * else compare dead animal count of previous epoch
       * relative to the elapsed time of current epoch
       */
      const isCurrentEpochLongerThanPrevious = currentPenState.duration > previousPenState.duration

      /*
       * compare elapsed time of the current epoch
       * to the elapsed time of previous epoch
       */
      const dayInMs = Duration.fromObject({ days: 1 }).as('milliseconds')
      const startTimeOfPreviousEpoch = DateTime.fromMillis(previousPenState.firstEventTimestamp)
      const elapsedTimeRelativeToPreviousEpoch = startTimeOfPreviousEpoch.plus({
        milliseconds: currentPenState.duration * dayInMs,
      })

      /* query dead animal count between the two timestamps
       *
       * if elapsed time of current epoch is longer than previous epoch,
       * query dead animal count of the entire previous epoch
       *
       * else query dead animal count of the previous epoch
       * relative to the elapsed time of the current epoch
       */
      const previousDeadAnimalCountRelative = await this.queryDeadAnimalCount(
        previousPenState.firstEventTimestamp,
        isCurrentEpochLongerThanPrevious ? previousPenState.lastEventTimestamp : elapsedTimeRelativeToPreviousEpoch.toMillis(),
        globalUniqueIdentifier
      )

      return previousDeadAnimalCountRelative
    },

    /**
     * Compares the number of dead animals in the current epoch to the previous epoch.
     *
     * @param {number} currentEpochDeadAnimalCount
     * The number of dead animals in the current epoch.
     * @param {number} previousEpochDeadAnimalCount
     * The number of dead animals in the previous epoch.
     * @returns {object}
     * An object containing the difference in dead animals,
     * percentage and the color red or green depending on the difference.
     */
    compareDeadAnimalsToPreviousEpoch(currentEpochDeadAnimalCount, previousEpochDeadAnimalCount) {
      if (isNaN(currentEpochDeadAnimalCount) || isNaN(previousEpochDeadAnimalCount)) return null

      const percentage = this.calculateDeadAnimalPercentageToPreviousEpoch(currentEpochDeadAnimalCount, previousEpochDeadAnimalCount)

      const isCurrentlyMore = currentEpochDeadAnimalCount - previousEpochDeadAnimalCount > 0

      return {
        isCurrentlyMore: isCurrentlyMore,
        difference: currentEpochDeadAnimalCount - previousEpochDeadAnimalCount,
        percentage: percentage > 0 ? `+${percentage}%` : `${percentage}%`,
      }
    },

    calculateDeadAnimalPercentageToPreviousEpoch(currentDeadAnimalCount, previousDeadAnimalCount) {
      // a negative count should never happen, but just in case
      if (currentDeadAnimalCount < 0 || previousDeadAnimalCount < 0) return 0

      // check if the previous count is 0 to avoid division by 0
      if (previousDeadAnimalCount === 0) return 0

      return Math.round(((currentDeadAnimalCount - previousDeadAnimalCount) / previousDeadAnimalCount) * 100)
    },

    async closeIncident(incidentId, forceClose = false) {
      const response = await backendHandler.put(useInstanceStore().selectedInstanceId, {
        path: '/api/incidents/' + incidentId,
        data: { forceClose },
      })
      if (!response.ok) return null
      return response.data
    },

    async reopenIncident(incidentId) {
      const response = await backendHandler.put(useInstanceStore().selectedInstanceId, { path: '/api/incidents/reopen/' + incidentId })
      if (!response.ok) return null
      return response.data
    },

    async updateGenericProperties(incidentId, genericProperties) {
      const response = await backendHandler.put(useInstanceStore().selectedInstanceId, {
        path: '/api/incidents/genericProperties/' + incidentId,
        data: genericProperties,
      })
      if (!response.ok) return null
      return response.data
    },
    async deleteIncident(incidentId) {
      const response = await backendHandler.delete(useInstanceStore().selectedInstanceId, { path: '/api/incidents/' + incidentId })
      if (!response.ok) return null
      return response.data
    },

    async assignIncidentToAccount(incidentId, accountId) {
      const response = await backendHandler.put(useInstanceStore().selectedInstanceId, {
        path: '/api/incidents/assign/' + incidentId + '/' + accountId,
      })
      return response.ok
    },

    async unAssignIncident(incidentId) {
      const response = await backendHandler.put(useInstanceStore().selectedInstanceId, { path: '/api/incidents/assign/' + incidentId + '/' })
      return response.ok
    },

    /**
     * Hide an incident.
     * @param incidentId
     * @param hideReason - The reason why the incident should be hidden.
     * @returns {Promise<Boolean>} - True if the incident was successfully hidden, false otherwise.
     */
    async hideIncident(incidentId, hideReason) {
      const response = await backendHandler.put(useInstanceStore().selectedInstanceId, {
        path: '/api/incidents/hide/' + incidentId,
        data: { reason: hideReason },
      })
      return response.ok
    },

    /**
     * Unhide an incident.
     * @param incidentId
     * @returns {Promise<Boolean>} - True if the incident was successfully unhidden, false otherwise.
     */
    async unHideIncident(incidentId) {
      const response = await backendHandler.delete(useInstanceStore().selectedInstanceId, { path: '/api/incidents/hide/' + incidentId })
      return response.ok
    },

    /**
     * loads the available displayConfigurations for creating/updating incidents
     * @param globalUniqueIdentifier
     * @returns {Promise<void>}
     */
    async loadDisplayConfigurations(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      const response = await backendHandler.get(globalUniqueIdentifier, { path: '/api/incidents/displayConfigurations' })
      if (!response.ok) return null
      return response.data
    },

    async loadAvailableStakeholders(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      const response = await backendHandler.get(globalUniqueIdentifier, {
        path: '/api/incidents/stakeholders',
      })
      if (!response.ok) return null
      return response.data
    },

    keepIncidentsAssignedToMeLoadedForInstance(globalUniqueIdentifier) {
      SyncableHandler.addSyncable(IncidentsAssignedToMeSyncable, globalUniqueIdentifier)
    },

    async ensureDeadAnimalCountsSyncable(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      await SyncableHandler.ensureSyncable(DeadAnimalCountsSyncable, globalUniqueIdentifier)
    },

    keepDeadAnimalCountsLoadedForInstance(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      SyncableHandler.addSyncable(DeadAnimalCountsSyncable, globalUniqueIdentifier)
    },

    async ensurePenStateDurationSyncable(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      await SyncableHandler.ensureSyncable(PenStateDurationSyncable, globalUniqueIdentifier)
    },

    async invalidateEvent(incidentEventId, globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      const response = await backendHandler.put(globalUniqueIdentifier, { path: '/api/incidentEvents/invalidate/' + incidentEventId })
      if (!response.ok) return null
      return response.data
    },
  },
})
